import { useMutationState } from '@tanstack/react-query';

const ORDER_ACTION_KEY = 'order-action';

export function orderActionMutationKey() {
  return [ORDER_ACTION_KEY] as const;
}

/**
 * Returns true if the given order has a mutation that is currently paused
 * (e.g. because the browser is offline) and waiting to retry once connection
 * is restored. React Query manages the actual queue + replay.
 *
 * We tag every order-action mutation with `mutationKey: ['order-action']` and
 * read the orderId from the mutation's runtime `state.variables` at match time
 * (since mutationKey is set when the hook is created, before we know which
 * order is being acted on).
 */
export function useOrderActionQueued(orderId: number): boolean {
  const states = useMutationState({
    filters: {
      predicate: (m) => {
        const key = m.options.mutationKey;
        if (!Array.isArray(key) || key[0] !== ORDER_ACTION_KEY) return false;
        const vars = m.state.variables as { orderId?: number } | undefined;
        return vars?.orderId === orderId;
      },
    },
    select: (m) => m.state.isPaused,
  });
  return states.some(Boolean);
}

/**
 * Total count of paused order-action mutations across the app.
 * Useful for global indicators (e.g. an "N actions queued" banner).
 */
export function useQueuedActionCount(): number {
  const states = useMutationState({
    filters: {
      predicate: (m) => {
        const key = m.options.mutationKey;
        return Array.isArray(key) && key[0] === ORDER_ACTION_KEY;
      },
    },
    select: (m) => m.state.isPaused,
  });
  return states.filter(Boolean).length;
}
